<template>
    <LoadingScreen v-if="loading" message="Loading trip…" />
    <main v-else class="trip-details">
        <div class="list-banner">
            <div class="overlay"></div>
            <h1 class="banner-title">{{ trip.name }}</h1>
        </div>
        <div class="max-width-container">
            <div class="action-bar">
                <div class="left">
                    <router-link to="/trips">
                        <Bttn
                            id="button-back"
                            icon="arrow_back"
                            icon-only
                            class="outlined"
                        />
                    </router-link>
                </div>
                <div class="right">
                    <Share :item-id="id" item-type="trip" class="outlined" />
                    <router-link :to="`/trips/${id}/edit`">
                        <Bttn icon="edit" icon-only class="filled" />
                    </router-link>
                </div>
            </div>
            <div class="trip-data">
                <div class="user-data-item timeframe">
                    <span class="icon material-symbols-outlined">
                        calendar_month
                    </span>
                    <div>
                        <span class="title">
                            Start Date
                            <template v-if="trip.end_date">— End Date</template>
                        </span>
                        <div class="user-input">
                            <span class="nowrap">
                                {{ $formatDate(trip.start_date, true) }}
                            </span>
                            <template v-if="trip.end_date">
                                —
                                <span class="nowrap">
                                    {{ $formatDate(trip.end_date, true) }}
                                </span>
                            </template>
                        </div>
                    </div>
                </div>
                <div v-if="trip.reason" class="user-data-item reason">
                    <span class="icon material-symbols-outlined">
                        question_mark
                    </span>
                    <div>
                        <span class="title">Travel Reason</span>
                        <div class="user-input">
                            {{ $enumGet("travel_reason", trip.reason) }}
                        </div>
                    </div>
                </div>
                <div v-if="trip.expenses" class="user-data-item expenses-total">
                    <span class="icon material-symbols-outlined">payments</span>
                    <div>
                        <span class="title">Total expenses</span>
                        <div class="user-input">
                            {{ userStore.user.currency
                            }}{{ $formatMoney(trip.expenses) }}
                        </div>
                    </div>
                </div>
                <div
                    v-if="trip.share < 1"
                    class="user-data-item expenses-share"
                >
                    <span class="icon material-symbols-outlined">payments</span>
                    <div>
                        <span class="title">Your expenses</span>
                        <div class="user-input">
                            {{ userStore.user.currency
                            }}{{ $formatMoney(getUserCost) }}
                        </div>
                    </div>
                </div>
                <div
                    v-if="trip.travel_agency"
                    class="user-data-item travel-agency"
                >
                    <span class="icon material-symbols-outlined">luggage</span>
                    <div>
                        <span class="title">Travel agency</span>
                        <div class="user-input">
                            {{ trip.travel_agency }}
                        </div>
                    </div>
                </div>
                <div
                    v-if="trip.start_mot"
                    class="user-data-item transport-start"
                >
                    <span class="icon material-symbols-outlined">
                        {{ $iconGet("transport", trip.start_mot) }}
                    </span>
                    <div>
                        <span class="title">Transport on the way there</span>
                        <div class="user-input">
                            {{ $enumGet("transport", trip.start_mot) }}
                            <template v-if="trip.start_mot_count > 1">
                                ({{ trip.start_mot_count }}x)
                            </template>
                        </div>
                    </div>
                </div>
                <div v-if="trip.end_mot" class="user-data-item transport-end">
                    <span class="icon material-symbols-outlined">
                        {{ $iconGet("transport", trip.end_mot) }}
                    </span>
                    <div>
                        <span class="title">Transport on the way back</span>
                        <div class="user-input">
                            {{ $enumGet("transport", trip.end_mot) }}
                            <template v-if="trip.end_mot_count > 1">
                                ({{ trip.end_mot_count }}x)
                            </template>
                        </div>
                    </div>
                </div>
            </div>
            <div class="trip-data">
                <div v-if="trip.notes" class="user-data-item full-width notes">
                    <span class="icon material-symbols-outlined">
                        text_snippet
                    </span>
                    <div>
                        <span class="title">Your notes</span>
                        <div class="user-input notes">{{ trip.notes }}</div>
                    </div>
                </div>
            </div>
            <div class="actions">
                <router-link class="edit-trip" :to="`/trips/${id}/edit`">
                    <Bttn>Edit trip</Bttn>
                </router-link>
                <router-link to="/trips">
                    <Bttn class="outlined">Back to trips</Bttn>
                </router-link>
            </div>
            <section class="map-and-location-list">
                <div class="point-map-wrapper">
                    <TripMap
                        v-if="!loading"
                        ref="locationsMap"
                        :geos="geos"
                        :highlighted="highlightedPredicate"
                        pickable
                        popup-on="click"
                        @map-pick="newLocationFromMap"
                    >
                        <template #popup="{ geos: hovered }">
                            <ul class="map-popup-card compact divided">
                                <MapPopupCard
                                    v-for="geo in sortedByDate(hovered)"
                                    :key="geo.getMeta().locationId"
                                    :title="geo.getMeta().name"
                                    :lines="locationLines(geo.getMeta())"
                                    clickable
                                    @title-click="pointClick([geo])"
                                />
                            </ul>
                        </template>
                    </TripMap>
                    <MobileMapOverlay />
                </div>
                <div class="trip-data">
                    <div class="user-data-item locations accent">
                        <span class="icon material-symbols-outlined">
                            location_on
                        </span>
                        <div>
                            <h2 class="title">
                                Locations
                                <button
                                    v-if="hasReorderableDates"
                                    type="button"
                                    class="sort-toggle"
                                    @click="toggleSorting"
                                >
                                    <span
                                        class="icon material-symbols-outlined"
                                    >
                                        {{ sorting ? "check" : "swap_vert" }}
                                    </span>
                                    {{ sorting ? "Done" : "" }}
                                </button>
                            </h2>
                            <span v-if="sorting" class="sort-save-hint">
                                <span class="icon material-symbols-outlined">
                                    info
                                </span>
                                Close to save
                            </span>
                            <LocationList
                                v-if="!loading && trip.locations.length"
                                :locations="trip.locations"
                                :groups="locationTimeline.groups"
                                :icons="locationTimeline.iconById"
                                :trip-icons="locationTimeline.tripIcons"
                                :sorting="sorting"
                                :trip="trip"
                                @hover="onHoverLocations"
                                @update:order="onOrderChanged"
                                @impression-updated="refreshMap"
                            />
                        </div>
                    </div>
                </div>
            </section>
            <div class="actions">
                <router-link
                    :to="`/trips/${id}/locations/add`"
                    class="add-location"
                >
                    <Bttn>Add location</Bttn>
                </router-link>
            </div>
            <LocationView
                v-for="(location, index) in trip.locations"
                :key="location.id"
                :location="location"
                :trip-id="trip.id"
                :role="locationTimeline.roleById[location.id]"
                :implied-mot="index === 0 ? impliedFirstLocationMot : null"
            />
            <div class="actions">
                <router-link
                    v-if="trip.locations.length >= 1"
                    :to="`/trips/${id}/locations/add`"
                >
                    <Bttn>Add location</Bttn>
                </router-link>
                <router-link to="/trips">
                    <Bttn class="outlined">Back to trips</Bttn>
                </router-link>
            </div>
        </div>
    </main>
</template>

<script>
import { useTripStore } from "js/stores/tripStore";
import { useUserStore } from "js/stores/userStore";
import { usePageErrorStore } from "js/stores/pageErrorStore";
import { pageErrorFrom } from "js/lib/server.js";
import TripMap from "js/components/maps/TripMap";
import MapPopupCard from "js/components/maps/MapPopupCard";
import MobileMapOverlay from "js/components/blocks/MobileMapOverlay";
import LocationList from "js/subpages/LocationList";
import LocationView from "js/subpages/LocationView";
import BaseFormWrapper from "js/components/base/BaseFormWrapper";
import LoadingScreen from "js/components/common/LoadingScreen";
import { Geo } from "js/lib/geocoding.js";
import { locationIcon, tripEdgeIcon } from "js/lib/locationIcons.js";
import { DAY_IN_MS } from "js/lib/dates.js";

function addDays(date, days) {
    return new Date(date.getTime() + days * DAY_IN_MS);
}

export default {
    name: "TripView",
    components: {
        TripMap,
        MapPopupCard,
        LocationList,
        LocationView,
        MobileMapOverlay,
        LoadingScreen,
    },
    extends: BaseFormWrapper,
    data() {
        return {
            id: null,
            loading: true,
            tripStore: undefined,
            userStore: undefined,
            trip: {},
            geos: [],
            sorting: false,
            hoveredLocationIds: [],
            pendingOrder: null,
        };
    },
    computed: {
        highlightedPredicate() {
            if (!this.hoveredLocationIds.length) {
                return null;
            }

            return (geo) =>
                this.hoveredLocationIds.includes(geo.getMeta().locationId);
        },
        getUserCost() {
            return Math.round(this.trip.share * this.trip.expenses);
        },
        impliedFirstLocationMot() {
            const tracksMot = Boolean(
                this.userStore?.user?.preferences?.track_mot
            );

            return tracksMot ? (this.trip.start_mot ?? null) : null;
        },
        locationTimeline() {
            const locations = this.trip.locations ?? [];

            const nextStayDate = new Array(locations.length).fill(null);
            let upcomingStayDate = null;
            for (let i = locations.length - 1; i >= 0; i--) {
                nextStayDate[i] = upcomingStayDate;
                if (locations[i].nights > 0) {
                    upcomingStayDate = locations[i].date;
                }
            }

            const tripHasStays = locations.some(
                (location) => location.nights > 0
            );

            const tracksMot = Boolean(
                this.userStore?.user?.preferences?.track_mot
            );
            const groups = [];
            const roleById = {};
            const iconById = {};
            let openStay = null;
            let pending = null;

            const pushPending = (location, kind, onTheWay = false) => {
                if (
                    pending &&
                    pending.type === kind &&
                    pending.onTheWay === onTheWay &&
                    pending.date === location.date
                ) {
                    pending.items.push(location);
                } else {
                    pending = {
                        type: kind,
                        onTheWay,
                        date: location.date,
                        items: [location],
                    };
                    groups.push(pending);
                }
                roleById[location.id] =
                    kind === "transit"
                        ? { type: kind, onTheWay, date: location.date }
                        : { type: kind, date: location.date };
            };

            const resolveIcon = (location, index, nested) => {
                iconById[location.id] = locationIcon(location, {
                    tracksMot,
                    nested,
                    isFirst: index === 0,
                    tripStartMot: this.trip.start_mot,
                });
            };

            locations.forEach((location, index) => {
                if (location.nights > 0) {
                    resolveIcon(location, index, false);
                    const start = new Date(location.date);
                    const end = addDays(start, location.nights);
                    openStay = {
                        type: "stay",
                        location,
                        start,
                        end,
                        dayTrips: [],
                    };
                    groups.push(openStay);
                    pending = null;
                    roleById[location.id] = {
                        type: "stay",
                        start,
                        end,
                        nights: location.nights,
                    };

                    return;
                }

                const date = new Date(location.date);
                if (openStay && date >= openStay.start && date < openStay.end) {
                    let dateGroup = openStay.dayTrips.find(
                        (group) => group.date === location.date
                    );
                    if (!dateGroup) {
                        dateGroup = { date: location.date, items: [] };
                        openStay.dayTrips.push(dateGroup);
                    }
                    dateGroup.items.push(location);
                    pending = null;
                    roleById[location.id] = {
                        type: "day-trip",
                        stayName: openStay.location.name,
                        date: location.date,
                    };
                    resolveIcon(location, index, true);

                    return;
                }

                resolveIcon(location, index, false);

                if (!tripHasStays) {
                    pushPending(location, "visit");

                    return;
                }

                const touchesNextStay =
                    nextStayDate[index] !== null &&
                    location.date === nextStayDate[index];

                pushPending(location, "transit", touchesNextStay);
            });

            const tripIcons = {
                start: tripEdgeIcon(this.trip.start_mot, tracksMot),
                end: tripEdgeIcon(this.trip.end_mot, tracksMot),
            };

            return { groups, roleById, iconById, tripIcons };
        },
        hasReorderableDates() {
            const counts = {};
            (this.trip.locations ?? []).forEach((location) => {
                counts[location.date] = (counts[location.date] ?? 0) + 1;
            });

            return Object.values(counts).some((count) => count > 1);
        },
    },
    async mounted() {
        this.tripStore = useTripStore();
        this.userStore = useUserStore();
        this.id = parseInt(this.$route.params.id);

        try {
            this.trip = this.tripStore.find(this.id) ?? (await this.getTrip());
            if (this.tripStore.hasDirtyLocations(this.id)) {
                this.trip.locations = await this.tripStore.refreshLocations(
                    this.id
                );
            }
        } catch (err) {
            usePageErrorStore().set(pageErrorFrom(err));
            this.loading = false;
            return;
        }
        this.refreshMap();
        this.loading = false;
    },
    methods: {
        async getTrip() {
            const trip = await this.tripStore.fetch(
                `/api/trips/${this.id}?with-locations`
            );
            this.tripStore.add(trip);

            return trip;
        },
        pointClick(geos) {
            this.$router.push(geos[0].getMeta().url);
        },
        sortedByDate(geos) {
            return [...geos].sort(
                (a, b) =>
                    a.getMeta().date.localeCompare(b.getMeta().date) ||
                    a.getMeta().locationId - b.getMeta().locationId
            );
        },
        locationLines(meta) {
            const format = (date) =>
                new Date(date).toLocaleDateString("en-us", {
                    day: "numeric",
                    month: "short",
                    timeZone: "UTC",
                });
            if (!meta.nights) {
                return [format(meta.date)];
            }
            const end = new Date(
                new Date(meta.date).getTime() + meta.nights * DAY_IN_MS
            );

            return [`${format(meta.date)} - ${format(end)}`];
        },
        refreshMap() {
            this.geos = this.trip.locations.map((location) => {
                return new Geo(location.geo).setMeta({
                    locationId: location.id,
                    name: location.name,
                    date: location.date,
                    nights: location.nights,
                    impression: location.impression,
                    isStay: location.nights > 0,
                    url: {
                        name: "tripView",
                        hash: `#location-${location.id}`,
                    },
                });
            });
        },
        onHoverLocations(locationIds) {
            this.hoveredLocationIds = locationIds;
        },
        onOrderChanged(order) {
            this.pendingOrder = order;
        },
        async toggleSorting() {
            if (!this.sorting) {
                this.sorting = true;
                this.pendingOrder = null;

                return;
            }

            const currentOrder = this.trip.locations.map(
                (location) => location.id
            );
            const changed =
                this.pendingOrder &&
                this.pendingOrder.some(
                    (id, index) => id !== currentOrder[index]
                );

            if (!changed) {
                this.sorting = false;

                return;
            }

            try {
                await this.tripStore.edit(`/api/trips/${this.trip.id}/`, {
                    location_order: this.pendingOrder,
                });
                this.trip.locations = await this.tripStore.refreshLocations(
                    this.trip.id
                );
                this.refreshMap();
                this.sorting = false;
            } catch (error) {
                this.trip.locations = await this.tripStore.refreshLocations(
                    this.trip.id
                );
                this.pendingOrder = null;
                this.handleResponseErrors(error);
            }
        },
        newLocationFromMap(longLat) {
            this.$router.push({
                name: "locationAdd",
                params: {
                    tripId: this.trip.id,
                },
                query: {
                    lon: longLat[0],
                    lat: longLat[1],
                },
            });
        },
    },
};
</script>
